import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CategoryPicker, MAX_CATEGORIES } from '@/components/CategoryPicker';
import { ApiError, type Category, submitPin } from '@/lib/api';

interface AddPinFormProps {
  position: { lat: number; lon: number };
  categories: Category[];
  onCancel: () => void;
  onSubmitted: () => void;
}

/**
 * The submission form.
 *
 * It collects a location, one to three categories, a title and a description.
 * It collects no name, no e-mail, no phone number — deliberately, and this is
 * why the whole flow stays outside consent territory and why the privacy
 * notice can say plainly that we hold no contact data for submitters. Do not
 * add a contact field without reworking Phase 7.
 */
export function AddPinForm({ position, categories, onCancel, onSubmitted }: AddPinFormProps) {
  /**
   * Selection order is preserved, not sorted: the first category the person
   * picks is the primary one and decides the pin's colour on the map.
   */
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      submitPin({
        ...position,
        categories: selected,
        title: title.trim(),
        description: description.trim(),
      }),
  });

  const issues = mutation.error instanceof ApiError ? (mutation.error.issues ?? []) : [];
  const issueFor = (field: string) => issues.find((i) => i.path === field)?.message;

  if (mutation.isSuccess) {
    return (
      <section
        aria-live="polite"
        className="rounded-lg border border-green-300 bg-green-50 p-4 shadow-lg"
      >
        <h2 className="text-lg font-semibold text-green-900">Podnět odeslán</h2>
        <p className="mt-2 text-sm text-green-900">{mutation.data.message}</p>
        <p className="mt-2 text-sm text-green-900">
          Na mapě se zobrazí až po schválení podle{' '}
          <Link className="underline" to="/pravidla">
            pravidel pro podněty
          </Link>
          .
        </p>
        <button
          type="button"
          onClick={onSubmitted}
          className="mt-3 rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-600"
        >
          Hotovo
        </button>
      </section>
    );
  }

  return (
    <form
      className="draft-form-highlight rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-lg font-semibold text-slate-900 focus:outline-none"
      >
        Nový podnět
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Umístění: {position.lat.toFixed(5)}, {position.lon.toFixed(5)} — přetažením značky na mapě
        ho můžete upravit.
      </p>

      <div className="mt-3 space-y-3">
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">
            Kategorie{' '}
            <span className="font-normal text-slate-500">
              (můžete vybrat až {MAX_CATEGORIES})
            </span>
          </legend>
          <p className="mt-0.5 text-xs text-slate-500">
            Mnoho problémů patří do více oblastí najednou. První vybraná kategorie určuje barvu
            bodu na mapě.
          </p>

          <div className="mt-2">
            <CategoryPicker
              categories={categories}
              selected={selected}
              onChange={setSelected}
              idPrefix="submit"
            />
          </div>

          {issueFor('categories') && (
            <p role="alert" className="mt-1 text-sm text-red-700">
              {issueFor('categories')}
            </p>
          )}
        </fieldset>

        <div>
          <label htmlFor="pin-title" className="block text-sm font-medium text-slate-800">
            Název problému
          </label>
          <input
            id="pin-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
            aria-describedby={issueFor('title') ? 'pin-title-error' : undefined}
            aria-invalid={issueFor('title') ? true : undefined}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="Např. Chybí přechod pro chodce"
          />
          {issueFor('title') && (
            <p id="pin-title-error" role="alert" className="mt-1 text-sm text-red-700">
              {issueFor('title')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="pin-description" className="block text-sm font-medium text-slate-800">
            Popis <span className="font-normal text-slate-500">(nepovinné)</span>
          </label>
          <textarea
            id="pin-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1200}
            rows={4}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
            placeholder="V čem přesně je problém a koho se týká."
          />
          <p className="mt-1 text-xs text-slate-500">{description.length} / 1200</p>
        </div>
      </div>

      {/*
        Stated before submission, not after. A submitter must know the bar
        before they write, which is also what makes the "osobní údaje třetích
        osob" rejection reason fair when it is used.
      */}
      <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
        Neuvádějte jména, adresy, SPZ ani jiné údaje o konkrétních lidech. Podněty zveřejňujeme po
        kontrole moderátorem a takové podněty zamítáme — viz{' '}
        <Link className="underline" to="/pravidla">
          pravidla
        </Link>
        .
      </p>

      {mutation.isError && issues.length === 0 && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {(mutation.error as Error).message}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending || selected.length === 0}
          className="flex-1 rounded-md bg-brand-700 px-4 py-2.5 font-semibold text-white hover:bg-brand-600 disabled:bg-slate-400"
        >
          {mutation.isPending ? 'Odesílám…' : 'Odeslat podnět'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-300 bg-white px-4 py-2.5 font-medium text-slate-700 hover:bg-slate-50"
        >
          Zrušit
        </button>
      </div>
    </form>
  );
}
